import React from "react";
import {
  Category,
  ChildCare,
  Description,
  Email,
  Instagram,
  LocationOn,
  Phone,
  Verified,
  TrendingUp,
  Link as LinkMuiIcon,
} from "@mui/icons-material";
import {
  Link,
  Grid,
  Avatar,
  Box,
  Chip,
  Typography,
  Tooltip,
} from "@mui/material";
import helpers from "../../../hooks/HelperFunctions";

function StatCard({ icon, label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        bgcolor: 'white',
        borderRadius: 1.5,
        px: 1.5,
        py: 1.25,
        boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
        border: '1px solid rgba(0,0,0,0.05)',
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ color: '#FF5200', mt: 0.2, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'Quicksand', fontWeight: 700, color: '#1a1a2e', fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value || '—'}
        </Typography>
      </Box>
    </Box>
  );
}

function InstaCollapes({ profile, numbers }) {
  const initials = ((profile.username || '').charAt(0).toUpperCase() + (profile.username || '').charAt(1).toLowerCase()) || '?';
  // `numbers` comes from users/viewedInfluencers, which now returns `contactNumbers`
  // (array) instead of a scalar `.number` — helper handles both shapes.
  const matchedNumberEntry = numbers.find((list) => list.username === profile.username);
  const maskedPhone = helpers.getPrimaryContactNumber(matchedNumberEntry) || '••••••••••';
  const followerCount = profile.instaFollowers
    ? Number(profile.instaFollowers) >= 1000000
      ? `${(Number(profile.instaFollowers) / 1000000).toFixed(1)}M`
      : Number(profile.instaFollowers) >= 1000
        ? `${(Number(profile.instaFollowers) / 1000).toFixed(1)}K`
        : profile.instaFollowers
    : '—';

  return (
    <Box
      sx={{
        background: 'linear-gradient(150deg, #f8faff 0%, #f3f0ff 40%, #fff5f0 100%)',
        borderRadius: 2,
        p: 2,
        mt: 0.5,
        border: '1px solid rgba(255,82,0,0.1)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
      }}
    >
      {/* Profile header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 1.5,
          borderBottom: '1px solid rgba(255,82,0,0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              bgcolor: '#FF5200',
              color: 'white',
              fontWeight: 800,
              fontSize: 16,
              boxShadow: '0 3px 10px rgba(255,82,0,0.35)',
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Link
                href={`https://www.instagram.com/${profile.username}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none', color: '#FF5200', fontWeight: 800, fontFamily: 'Quicksand', fontSize: '0.95rem' }}
              >
                @{profile.username}
              </Link>
              {profile.is_verified && (
                <Verified sx={{ color: '#0095f6', fontSize: 15 }} />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Quicksand', fontWeight: 600 }}>
              {profile.full_name || ''}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="View on Instagram">
          <Link
            href={`https://www.instagram.com/${profile.username}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'flex', alignItems: 'center', color: '#E1306C', '&:hover': { color: '#C13584' } }}
          >
            <Instagram sx={{ fontSize: 22 }} />
          </Link>
        </Tooltip>
      </Box>

      {/* Stats row */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <StatCard
          icon={<TrendingUp sx={{ fontSize: 16 }} />}
          label="Followers"
          value={followerCount}
        />
        <StatCard
          icon={<LocationOn sx={{ fontSize: 16 }} />}
          label="Location"
          value={[profile.city, profile.state].filter(Boolean).join(', ') || '—'}
        />
        <StatCard
          icon={<Category sx={{ fontSize: 16 }} />}
          label="Category"
          value={profile.categoryName}
        />
        <StatCard
          icon={<ChildCare sx={{ fontSize: 16 }} />}
          label="DOB"
          value={profile.dob}
        />
      </Box>

      {/* Contact + link row */}
      <Grid container spacing={1} sx={{ mb: profile.biography ? 1.5 : 0 }}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <StatCard icon={<Email sx={{ fontSize: 16 }} />} label="Email" value={profile.email} />
            <StatCard icon={<Phone sx={{ fontSize: 16 }} />} label="Phone" value={maskedPhone} />
          </Box>
        </Grid>
        {profile.external_url && (
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                bgcolor: 'white',
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                border: '1px solid rgba(0,0,0,0.05)',
                height: '100%',
              }}
            >
              <Box sx={{ color: '#FF5200', mt: 0.2, flexShrink: 0 }}><LinkMuiIcon sx={{ fontSize: 16 }} /></Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                  External Link
                </Typography>
                <Link
                  href={profile.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: 'Quicksand', fontWeight: 700, color: '#FF5200', fontSize: '0.78rem', wordBreak: 'break-all' }}
                >
                  {profile.external_url}
                </Link>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Bio */}
      {profile.biography && (
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 1.5,
            px: 1.5,
            py: 1.25,
            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            border: '1px solid rgba(0,0,0,0.05)',
            mb: profile.manager?.length ? 1.5 : 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Description sx={{ fontSize: 15, color: '#FF5200' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Bio
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontFamily: 'Quicksand', color: '#374151', lineHeight: 1.6, fontSize: '0.82rem' }}>
            {profile.biography}
          </Typography>
        </Box>
      )}

      {/* Manager section */}
      {profile.manager?.length > 0 && (
        <Box sx={{ bgcolor: 'white', borderRadius: 1.5, px: 1.5, py: 1.25, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
            Manager
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {profile.manager.map((mgr, i) => (
              <Chip
                key={i}
                label={`${mgr.manager_name} · ${mgr.manager_number}`}
                size="small"
                sx={{ fontFamily: 'Quicksand', fontWeight: 600, fontSize: '0.72rem', bgcolor: 'rgba(255,82,0,0.08)', color: '#CC4200', border: '1px solid rgba(255,82,0,0.2)' }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default InstaCollapes;
